import React, { useState, useEffect, useRef } from 'react';
import SettingsPanel from './components/SettingsPanel';
import IntroPage from './components/IntroPage';
import UploadPage from './components/UploadPage';
import ContinuePage from './components/ContinuePage';
import NetBankPage from './components/NetBankPage';
import NetLabPage from './components/NetLabPage';
import Banner from './components/Banner';
import TaskModal from './components/TaskModal';
import LogModal from './components/LogModal';
import ResultPage from './components/ResultPage';
import { BACKEND_URL, TASK_STATUS, apiFetch } from './constants';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Login from './auth/Login';

function AppInner() {
  const { logout, user } = useAuth();
  const [mode, setMode] = useState(null);
  const iframeRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [resultTask, setResultTask] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [logs, setLogs] = useState('');
  const logSourceRef = useRef(null);
  const tasksRef = useRef([]);
  const [uploadFolder, setUploadFolder] = useState('');
  const [options, setOptions] = useState({
    graph: false,
    lv: false,
    mv: false,
    power: false,
    showTiles: true,

    verbose: true,
    evProfiles: false,
    hpProfiles: false,
    includePvProfiles: true,
    timestep: 'daily',
    voltageThreshold: 0.08,

    stdHeight: 4.5,
    stdSurfaceThreshold: 50,
    loadConnectingTolerance: 150,
    ebuMvDistanceTolerance: 15000,
    lineLengthThreshold: 100,
    standardLineLength: 30,
    leafNodeConnection: 150,

    lvMvLoadSeparation: 500,
    simultaneityFactor: 0.7,
    lvTrafoThreshold: 1.1,
    powerFactor: 0.9,

    packingFactor: 0.8,
    panelEfficiency: 20,
    personEnergyFactor: 1000,
    stdExtraPersonEnergyFactor: 550,
    surfaceEnergyFactor: 5,
    hProfileTolerance: 200,
    overshoot: 0.05,

    intermediatePrint: true,
    intermediateResults: true,
    graphModel: true,
    powerFactoryModel: false,

    maxValidPenalty: 300,
    communityBuffer: 600,
    tolerance: 0.01,
    maxIterations: 20,

    genSize: 20,
    penaltyWeight: 100
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState(null);
  const [uploadHtmlFiles, setUploadHtmlFiles] = useState([]);
  const [missingResults, setMissingResults] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadTaskId, setUploadTaskId] = useState(null);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = params.get('mode');
    const task = params.get('task');
    const taskName = params.get('taskName');
    if (m === 'result') {
      const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
      };

      const metadata = params.get('netbankArea')
        ? {
            level: (params.get('areaLevel') || '').toUpperCase() || null,
            nodes: parseNumber(params.get('areaNodes')),
            grids: parseNumber(params.get('areaGrids')),
            pv: params.get('areaPv') === 'yes' ? 'yes' : params.get('areaPv') === 'no' ? 'no' : null
          }
        : undefined;

      setResultTask({
        id: task || null,
        name: taskName || task || 'NetBank result',
        metadata
      });
      setMode('result');
    } else if (m === 'netlab') {
      setMode('netlab');
    }

    const handlePop = (e) => {
      const modeState = e.state ? e.state.mode : null;
      setMode(modeState);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  useEffect(() => {
    if (uploadFiles && uploadFiles.length > 0) {
      const rel = uploadFiles[0].webkitRelativePath || uploadFiles[0].name;
      setUploadFolder(rel.split('/')[0]);
    }
  }, [uploadFiles]);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await apiFetch('/tasks');
        if (res.ok) {
          const updated = await res.json();
          setTasks(updated);
        }
      } catch (err) {
        console.error('Failed to fetch tasks', err);
      }
    };

    const interval = setInterval(() => {
      if (tasksRef.current.some(t => t.status === TASK_STATUS.RUNNING || t.status === TASK_STATUS.STOPPING)) {
        fetchTasks();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const startGeneration = async () => {
    const payload = await iframeRef.current?.contentWindow.getSelectedPayload();
    if (!payload) {
      alert('Please select at least one tile!');
      return;
    }

    const body = { ...payload, settings: options };
    setGenerating(true);
    try {
      const res = await apiFetch('/start-generation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (data.task_id) {
        setTasks(prev => [...prev, { id: data.task_id, name: payload.display_name || payload.names[0], status: TASK_STATUS.RUNNING }]);
      } else {
        alert('Generation failed to start. Please try again.');
      }
    } catch (err) {
      console.error('FETCH ERROR:', err);
      alert('Could not reach the server. Please check the backend is running.');
    } finally {
      setGenerating(false);
    }
  };

  const cancelTask = async (id) => {
    try {
      await apiFetch(`/stop-generation/${id}`, { method: 'POST' });
      setTasks(prev =>
        prev.map(t => t.id === id ? { ...t, status: TASK_STATUS.STOPPING } : t)
      );
    } catch (err) {
      console.error('Failed to stop task', err);
      alert('Could not cancel the task. Please try again.');
    }
  };

  const deleteTask = async (id) => {
    try {
      const res = await apiFetch(`/tasks/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete failed');
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Failed to delete task from backend', err);
      alert('Could not delete the task. Please try again.');
    }
  };

  const selectTask = (task) => {
    setTaskOpen(false);
    const url = `?mode=result&task=${encodeURIComponent(task.id)}&taskName=${encodeURIComponent(task.name)}`;
    window.open(url, '_blank');
  };

  const showLog = (task) => {
    setLogs('');
    setLogOpen(true);
    if (logSourceRef.current) {
      logSourceRef.current.close();
      logSourceRef.current = null;
    }

    const taskId = encodeURIComponent(task.id);
    const logUrl = `/download?file=${taskId}/backend.log`;

    // Track whether the modal was closed before the fetch completed
    let cancelled = false;

    apiFetch(logUrl, { cache: 'no-store' })
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) setLogs('[no log file yet]\n');
          return;
        }
        const txt = await res.text();
        if (!cancelled) setLogs(txt.endsWith('\n') ? txt : txt + '\n');
      })
      .catch(() => {
        if (!cancelled) setLogs('[failed to load existing log]\n');
      })
      .finally(() => {
        if (cancelled) return; // modal was closed before fetch completed — don't create SSE

        // withCredentials: EventSource defaults this to false, so without
        // it the session cookie never gets sent on this request (it's
        // cross-origin between the dev server and the backend) and the
        // auth guard rejects it with 401 on every attempt -- unlike
        // apiFetch's regular calls, which already set credentials:'include'.
        const src = new EventSource(`${BACKEND_URL}/logs/${taskId}`, { withCredentials: true });

        src.onmessage = (e) => setLogs((prev) => prev + e.data + '\n');

        src.addEventListener('end', (e) => {
          const finalStatus = (e.data || '').trim() || TASK_STATUS.DONE;
          setLogs(prev => prev + `\n[task ${finalStatus}]\n`);
          setTasks(prev =>
            prev.map(t => t.id === task.id ? { ...t, status: finalStatus } : t)
          );
          src.close();
        });

        src.onerror = () => {
          // EventSource auto-reconnects on its own after a transient drop
          // (network blip, proxy idle timeout -- routine on a multi-minute
          // generation run) and resumes cleanly via Last-Event-ID. Only
          // treat this as fatal once the browser itself has given up
          // (readyState CLOSED) -- closing on every error here defeats
          // that reconnect and is why the log used to go quiet until the
          // modal was closed and reopened.
          if (src.readyState === EventSource.CLOSED) {
            setLogs((prev) => prev + '\n[log stream error or closed]\n');
          }
        };

        logSourceRef.current = src;
      });

    // Return a cancel function so closeLog can suppress the finally block
    return () => { cancelled = true; };
  };

  const closeLog = () => {
    if (logSourceRef.current) {
      logSourceRef.current.close();
      logSourceRef.current = null;
    }
    setLogOpen(false);
  };

  const startContinueTask = async (step, taskId) => {
    const id = taskId || uploadTaskId;
    if (!id) {
      alert('No task selected.');
      return;
    }
    try {
      const res = await apiFetch('/continue-generation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: id, step, settings: options }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Server error ${res.status}`);
      }
      const data = await res.json();
      const name = `${id} — ${step.replace(/_/g, ' ')}`;
      setTasks(prev => [...prev, { id: data.task_id || id, name, status: TASK_STATUS.RUNNING }]);
      setTaskOpen(true);
    } catch (err) {
      console.error('Continue task error:', err);
      alert(`Could not start continuation: ${err.message}`);
    }
  };

  const goHome = () => {
    window.history.pushState({ mode: null }, '');
    setMode(null);
  };

  const hasDone = tasks.some(t => t.status === TASK_STATUS.DONE);
  const runningCount = tasks.filter(t => t.status === TASK_STATUS.RUNNING).length;

  if (!mode) {
    return (
      <IntroPage
        onCreate={() => {
          window.history.pushState({ mode: 'create' }, '');
          setMode('create');
        }}
        onUpload={() => {
          window.history.pushState({ mode: 'upload' }, '');
          setMode('upload');
        }}
        onNetbank={() => {
          window.history.pushState({ mode: 'netbank' }, '');
          setMode('netbank');
        }}
        onNetlab={() => window.open('?mode=netlab', '_blank', 'noopener')}
        user={user}
        onLogout={logout}
      />
    );
  }

  if (mode === 'upload') {
    return (
      <>
        <Banner
          onIconClick={() => setTaskOpen(true)}
          onLogoClick={goHome}
          hasDone={hasDone}
          runningCount={runningCount}
          user={user}
          onLogout={logout}
        />
        <UploadPage />
        <TaskModal
          open={taskOpen}
          tasks={tasks}
          onCancel={cancelTask}
          onDelete={deleteTask}
          onSelect={selectTask}
          onShowLog={showLog}
          onClose={() => setTaskOpen(false)}
        />
        <LogModal open={logOpen} logs={logs} onClose={closeLog} />
      </>
    );
  }

  if (mode === 'continue') {
    return (
      <>
        <Banner
          onIconClick={() => setTaskOpen(true)}
          onLogoClick={goHome}
          hasDone={hasDone}
          runningCount={runningCount}
          user={user}
          onLogout={logout}
        />
        <ContinuePage files={uploadFiles} onRun={startContinueTask} />
        <TaskModal
          open={taskOpen}
          tasks={tasks}
          onCancel={cancelTask}
          onDelete={deleteTask}
          onSelect={selectTask}
          onShowLog={showLog}
          onClose={() => setTaskOpen(false)}
        />
      </>
    );
  }

  if (mode === 'netbank') {
    return (
      <>
        <Banner
          onIconClick={() => setTaskOpen(true)}
          onLogoClick={goHome}
          hasDone={hasDone}
          runningCount={runningCount}
          user={user}
          onLogout={logout}
        />
        <NetBankPage />
        <TaskModal
          open={taskOpen}
          tasks={tasks}
          onCancel={cancelTask}
          onDelete={deleteTask}
          onSelect={selectTask}
          onShowLog={showLog}
          onClose={() => setTaskOpen(false)}
        />
        <LogModal open={logOpen} logs={logs} onClose={closeLog} />
      </>
    );
  }

  if (mode === 'result') {
    return <ResultPage task={resultTask} />;
  }

  if (mode === 'netlab') {
    return <NetLabPage user={user} onLogout={logout} />;
  }

  return (
    <>
      <Banner
        onIconClick={() => setTaskOpen(true)}
        onLogoClick={goHome}
        hasDone={hasDone}
        runningCount={runningCount}
        user={user}
        onLogout={logout}
      />
      <div id="app-container">
        <iframe
          ref={iframeRef}
          src={process.env.PUBLIC_URL + '/interactive_map_advanced.html'}
          title="Map"
          style={{ width: '100%', height: 'calc(100vh - 64px)', border: 'none' }}
        />
        <button
          id="run-python"
          onClick={startGeneration}
          disabled={generating}
        >
          {generating ? 'Starting…' : 'Run Grid Generation'}
        </button>
        <SettingsPanel
          options={options}
          setOptions={setOptions}
          open={settingsOpen}
          setOpen={setSettingsOpen}
        />
      </div>
      <TaskModal
        open={taskOpen}
        tasks={tasks}
        onCancel={cancelTask}
        onDelete={deleteTask}
        onSelect={selectTask}
        onShowLog={showLog}
        onClose={() => setTaskOpen(false)}
      />
      <LogModal open={logOpen} logs={logs} onClose={closeLog} />
    </>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!user) return <Login />;
  return <AppInner />;
}

export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
